import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Mail, ArrowLeft, CheckCircle, Phone } from 'lucide-react'
import { parsePhoneNumber, isValidPhoneNumber } from 'libphonenumber-js'
import { useAuth } from '../../contexts/AuthContext'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import TopNavigation from '../../../components/TopNavigation'

const ForgotPasswordPage: React.FC = () => {
  const { resetPassword } = useAuth()
  const navigate = useNavigate()
  const [identifier, setIdentifier] = useState('')
  const [resetMethod, setResetMethod] = useState<'email' | 'phone'>('email')
  const [loading, setLoading] = useState(false)
  const [resetSent, setResetSent] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      if (resetMethod === 'phone') {
        // Validate and format phone number to E.164
        if (!isValidPhoneNumber(identifier, 'US')) {
          setError('Please enter a valid US phone number')
          setLoading(false)
          return
        }

        const phoneNumber = parsePhoneNumber(identifier, 'US')
        const e164Phone = phoneNumber.format('E.164')

        // Send OTP via SMS
        await resetPassword(e164Phone, 'phone')

        // Redirect to phone verification page with the formatted phone
        navigate(`/verify-phone?phone=${encodeURIComponent(e164Phone)}`)
      } else {
        // Email reset - sends magic link
        await resetPassword(identifier, 'email')
        setResetSent(true)
      }
    } catch (error: any) {
      setError(error.message || `Failed to send password reset ${resetMethod === 'email' ? 'email' : 'SMS'}`)
    } finally {
      setLoading(false)
    }
  }

  if (resetSent) {
    return (
      <div className="min-h-screen-safe bg-background flex flex-col">
        <TopNavigation backTo="/login" backText="Back to Login" />
        
        <div className="flex-1 flex items-center justify-center px-4 py-12">
          <div className="w-full max-w-md">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-[#962831]/10 mb-6">
                <CheckCircle className="h-8 w-8 text-[#962831]" />
              </div>

              <h1 className="text-2xl font-bold text-foreground mb-2">
                {resetMethod === 'email' ? 'Check Your Email' : 'Check Your Phone'}
              </h1>

              <p className="text-muted-foreground mb-6">
                We've sent a password reset {resetMethod === 'email' ? 'link' : 'code'} to <strong>{identifier}</strong>
              </p>

              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  {resetMethod === 'email'
                    ? "Didn't receive the email? Check your spam folder or try again."
                    : "Didn't receive the SMS? Check your phone or try again."}
                </p>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => {
                      setResetSent(false)
                      setIdentifier('')
                    }}
                    className="flex-1 px-4 py-2 text-sm font-medium text-[#962831] border border-[#962831] rounded-md hover:bg-[#962831]/5 transition-colors"
                  >
                    Try Different {resetMethod === 'email' ? 'Email' : 'Phone'}
                  </button>

                  <Link
                    to="/login"
                    className="flex-1 px-4 py-2 text-sm font-medium text-center bg-[#962831] rounded-md transition-colors hover:opacity-90"
                  >
                    <span style={{ color: '#ffffff' }}>Back to Login</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen-safe bg-background flex flex-col">
      <TopNavigation backTo="/login" backText="Back to Login" />
      
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">
              Forgot Password?
            </h1>
            <p className="text-muted-foreground">
              No worries! Enter your {resetMethod === 'email' ? 'email address' : 'phone number'} and we'll send you a reset {resetMethod === 'email' ? 'link' : 'code'}.
            </p>
          </div>

          {/* Toggle between Email and Phone */}
          <div className="flex gap-2 mb-6">
            <button
              type="button"
              onClick={() => {
                setResetMethod('email')
                setIdentifier('')
                setError('')
              }}
              className={`flex-1 py-2 px-4 rounded-md font-medium transition-colors ${
                resetMethod === 'email'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
              }`}
            >
              <Mail className="inline h-4 w-4 mr-2" />
              Email
            </button>
            <button
              type="button"
              onClick={() => {
                setResetMethod('phone')
                setIdentifier('')
                setError('')
              }}
              className={`flex-1 py-2 px-4 rounded-md font-medium transition-colors ${
                resetMethod === 'phone'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
              }`}
            >
              <Phone className="inline h-4 w-4 mr-2" />
              Phone
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="identifier" className="block text-sm font-medium text-foreground mb-2">
                {resetMethod === 'email' ? 'Email Address' : 'Phone Number'}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  {resetMethod === 'email' ? (
                    <Mail className="h-5 w-5 text-muted-foreground" />
                  ) : (
                    <Phone className="h-5 w-5 text-muted-foreground" />
                  )}
                </div>
                <input
                  id="identifier"
                  type={resetMethod === 'email' ? 'email' : 'tel'}
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  className="block w-full pl-10 pr-3 py-3 border border-input rounded-md bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                  placeholder={resetMethod === 'email' ? 'Enter your email address' : 'Enter your phone number'}
                  required
                  disabled={loading}
                />
              </div>
            </div>

            {error && (
              <div className="p-3 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !identifier}
              className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  {resetMethod === 'email' ? 'Sending Reset Link...' : 'Sending Reset Code...'}
                </>
              ) : (
                resetMethod === 'email' ? 'Send Reset Link' : 'Send Reset Code'
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground">
              Remember your password?{' '}
              <Link 
                to="/login" 
                className="text-primary hover:text-primary/80 font-medium transition-colors"
              >
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ForgotPasswordPage
